import { useEffect, useMemo, useRef, useState } from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { buildAdsenseScriptUrl } from "@/lib/adsense";
import { loadHeadScriptOnce } from "@/lib/scriptLoader";
import type { AdProvider, AdSlotKey } from "@/lib/adSettings";

interface ArticleAdSlotProps {
  slotKey: AdSlotKey;
  minHeightClassName: string;
  className?: string;
  scrollTriggerPercent?: number;
}

declare global {
  interface Window {
    adsbygoogle?: unknown[];
    googletag?: {
      cmd: Array<() => void>;
      defineSlot?: (adUnitPath: string, size: [number, number], divId: string) => {
        addService: (service: unknown) => unknown;
      } | null;
      pubads?: () => unknown;
      enableServices?: () => void;
      display?: (divId: string) => void;
      _afiliabrServicesEnabled?: boolean;
    };

  }
}

const displayedGamSlots = new Set<string>();

function parseAdSize(formatLabel: string): [number, number] {
  const match = formatLabel.match(/(\d+)\D+(\d+)/);
  if (!match) return [720, 90];
  return [Number(match[1]), Number(match[2])];
}

function isSafeScriptUrl(provider: AdProvider, value: string) {
  if (!value) return false;

  try {
    const url = new URL(value);
    if (url.protocol !== "https:") return false;

    const hostname = url.hostname.toLowerCase();
    if (provider === "adsense") return hostname.includes("googlesyndication.com");
    if (provider === "gam") return hostname.includes("doubleclick.net");
    if (provider === "monetag") return hostname.includes("monetag") || hostname.includes("onclckmn.com");
    if (provider === "ezoic") return hostname.includes("ezoic");
    if (provider === "mediavine") return hostname.includes("mediavine");

    return false;
  } catch {
    return false;
  }
}

function buildGamAdUnitPath(slotCode: string, networkCode: string) {
  if (!slotCode) return "";
  if (slotCode.startsWith("/")) return slotCode;
  if (!networkCode) return slotCode;
  return `/${networkCode}/${slotCode}`;
}

export default function ArticleAdSlot({
  slotKey,
  minHeightClassName,
  className = "",
  scrollTriggerPercent = 0.25,
}: ArticleAdSlotProps) {
  const { settings } = useSiteSettings();
  const adSettings = settings.adSettings;
  const slot = adSettings.slots[slotKey];
  const containerRef = useRef<HTMLDivElement | null>(null);
  const adsenseRef = useRef<HTMLModElement | null>(null);

  const [isInView, setIsInView] = useState(false);
  const [hasReachedScrollTrigger, setHasReachedScrollTrigger] = useState(!adSettings.lazyLoadEnabled || scrollTriggerPercent <= 0);
  const [isActivated, setIsActivated] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  const formatLabel = slot.formatLabel;
  const safeScriptUrl = useMemo(() => {
    if (slot.provider === "adsense") {
      const adsense = adSettings.providers.adsense;
      return adsense.clientId && isSafeScriptUrl("adsense", adsense.scriptUrl)
        ? buildAdsenseScriptUrl(adsense.scriptUrl, adsense.clientId)
        : "";
    }

    if (slot.provider === "gam") {
      const gam = adSettings.providers.gam;
      return isSafeScriptUrl("gam", gam.scriptUrl) ? gam.scriptUrl : "";
    }

    if (slot.provider === "monetag") {
      return isSafeScriptUrl("monetag", adSettings.providers.monetag.scriptUrl) ? adSettings.providers.monetag.scriptUrl : "";
    }

    if (slot.provider === "ezoic") {
      return isSafeScriptUrl("ezoic", adSettings.providers.ezoic.scriptUrl) ? adSettings.providers.ezoic.scriptUrl : "";
    }

    if (slot.provider === "mediavine") {
      return isSafeScriptUrl("mediavine", adSettings.providers.mediavine.scriptUrl) ? adSettings.providers.mediavine.scriptUrl : "";
    }

    return "";
  }, [adSettings.providers, slot.provider]);

  const configurationReady = useMemo(() => {
    if (!slot.enabled) return false;

    if (slot.provider === "adsense") {
      return Boolean(adSettings.providers.adsense.enabled && adSettings.providers.adsense.clientId && slot.slotCode);
    }

    if (slot.provider === "gam") {
      return Boolean(adSettings.providers.gam.enabled && slot.slotCode);
    }

    if (slot.provider === "monetag") {
      return Boolean(adSettings.providers.monetag.enabled && slot.slotCode);
    }

    if (slot.provider === "ezoic") {
      return Boolean(adSettings.providers.ezoic.enabled && slot.containerId);
    }

    return Boolean(adSettings.providers.mediavine.enabled && slot.containerId);
  }, [adSettings.providers, slot]);

  useEffect(() => {
    if (!slot.enabled) return;
    if (!adSettings.lazyLoadEnabled || scrollTriggerPercent <= 0) return;

    const updateScrollState = () => {
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (documentHeight <= 0) {
        setHasReachedScrollTrigger(true);
        return;
      }

      const progress = window.scrollY / documentHeight;
      if (progress >= scrollTriggerPercent) {
        setHasReachedScrollTrigger(true);
      }
    };

    updateScrollState();
    window.addEventListener("scroll", updateScrollState, { passive: true });

    return () => {
      window.removeEventListener("scroll", updateScrollState);
    };
  }, [adSettings.lazyLoadEnabled, scrollTriggerPercent, slot.enabled]);

  useEffect(() => {
    if (!slot.enabled) return;

    const element = containerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setIsInView(entry.isIntersecting);
      },
      {
        rootMargin: adSettings.lazyLoadEnabled ? "160px 0px" : "0px",
        threshold: 0.15,
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [adSettings.lazyLoadEnabled, slot.enabled]);

  useEffect(() => {
    if (!slot.enabled) return;
    if (!isActivated && isInView && hasReachedScrollTrigger) {
      setIsActivated(true);
    }
  }, [hasReachedScrollTrigger, isActivated, isInView, slot.enabled]);

  useEffect(() => {
    if (!isActivated || !safeScriptUrl) return;

    const scriptId = slot.provider === "adsense" ? "ad-provider-adsense" : `ad-provider-${slot.provider}`;
    const attrs = slot.provider === "adsense" ? { crossorigin: "anonymous" } : {};

    loadHeadScriptOnce(scriptId, safeScriptUrl, attrs)
      .then(() => setScriptLoaded(true))
      .catch(() => setScriptLoaded(false));
  }, [isActivated, safeScriptUrl, slot.provider]);

  useEffect(() => {
    if (!isActivated || !configurationReady || slot.provider !== "adsense" || !scriptLoaded) return;
    const node = adsenseRef.current;
    if (!node || node.dataset.rendered === "true") return;

    try {
      window.adsbygoogle = window.adsbygoogle || [];
      window.adsbygoogle.push({});
      node.dataset.rendered = "true";
    } catch {
      node.dataset.rendered = "false";
    }
  }, [configurationReady, isActivated, scriptLoaded, slot.provider]);

  useEffect(() => {
    if (!isActivated || !configurationReady || slot.provider !== "gam" || !scriptLoaded) return;

    const adUnitPath = buildGamAdUnitPath(slot.slotCode, adSettings.providers.gam.networkCode);
    if (!adUnitPath || displayedGamSlots.has(slot.containerId)) return;

    const [width, height] = parseAdSize(formatLabel);
    window.googletag = window.googletag || { cmd: [] };

    window.googletag.cmd.push(() => {
      if (!window.googletag) return;
      if (displayedGamSlots.has(slot.containerId)) return;

      if (!window.googletag.defineSlot || !window.googletag.pubads || !window.googletag.enableServices || !window.googletag.display) {
        return;
      }

      const definedSlot = window.googletag.defineSlot(adUnitPath, [width, height], slot.containerId);
      if (!definedSlot) return;

      definedSlot.addService(window.googletag.pubads());

      if (!window.googletag._afiliabrServicesEnabled) {
        window.googletag.enableServices();
        window.googletag._afiliabrServicesEnabled = true;
      }

      window.googletag.display(slot.containerId);
      displayedGamSlots.add(slot.containerId);

    });
  }, [adSettings.providers.gam.networkCode, configurationReady, formatLabel, isActivated, scriptLoaded, slot.containerId, slot.provider, slot.slotCode]);

  if (!slot.enabled) {
    return null;
  }

  const renderActivatedContent = () => {
    if (!configurationReady) {
      return (
        <StatusCard
          icon="ri-settings-3-line"
          title="Slot pronto para integração"
          description={`Configure o código do slot ${slot.name} no painel para ativar ${slot.provider.toUpperCase()}.`}
        />
      );
    }

    if (slot.provider === "adsense") {
      return (
        <div className="w-full h-full bg-white flex items-center justify-center">
          <ins
            ref={adsenseRef}
            className="adsbygoogle block w-full h-full"
            style={{ display: "block", width: "100%", height: "100%" }}
            data-ad-client={adSettings.providers.adsense.clientId}
            data-ad-slot={slot.slotCode}
            data-full-width-responsive="false"
          />

        </div>
      );
    }

    if (slot.provider === "gam") {
      return <div id={slot.containerId} className="w-full h-full bg-white" />;
    }

    if (slot.provider === "monetag") {
      return (
        <div
          id={slot.containerId}
          className="w-full h-full bg-white"
          data-monetag-slot={slot.slotCode}
          data-monetag-secondary={slot.secondaryCode}
          data-monetag-format={formatLabel}
        />
      );
    }

    if (slot.provider === "ezoic") {
      return (
        <div
          id={slot.containerId}
          className="w-full h-full bg-white"
          data-ez-placeholder={slot.slotCode || slot.containerId}
          data-ad-format={formatLabel}
        />
      );
    }

    return (
      <div
        id={slot.containerId}
        className="w-full h-full bg-white"
        data-mediavine-slot={slot.slotCode || slot.containerId}
        data-mediavine-site={adSettings.providers.mediavine.siteId}
        data-ad-format={formatLabel}
      />
    );
  };

  return (
    <div
      ref={containerRef}
      data-ad-slot-key={slotKey}
      data-ad-provider={slot.provider}
      data-ad-format={formatLabel}
      data-ad-state={isActivated ? "active" : "idle"}
      data-scroll-trigger={scrollTriggerPercent}
      className={`rounded-2xl border border-dashed overflow-hidden ${minHeightClassName} ${className}`}
    >
      <div className="relative w-full h-full bg-gradient-to-b from-gray-50 to-white">
        {isActivated ? (
          <>
            {renderActivatedContent()}
            <div className="pointer-events-none absolute top-3 right-3 rounded-full bg-black/65 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white">
              {slot.provider}
            </div>
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center px-6 text-center">
            <div className="w-10 h-10 flex items-center justify-center mb-2 rounded-full bg-gray-100 text-gray-400">
              <i className="ri-loader-4-line text-xl"></i>
            </div>
            <span className="text-gray-500 text-xs font-semibold">Lazy Load preparado</span>
            <span className="text-gray-400 text-[11px] mt-1 max-w-xs">
              O slot {slot.name} será carregado quando entrar na tela{adSettings.lazyLoadEnabled && scrollTriggerPercent > 0 ? ` e após ${Math.round(scrollTriggerPercent * 100)}% de scroll` : ""}.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusCard({ icon, title, description }: { icon: string; title: string; description: string }) {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center px-6 text-center">
      <div className="w-10 h-10 flex items-center justify-center mb-2 rounded-full bg-orange-50 text-orange-500">
        <i className={`${icon} text-xl`}></i>
      </div>
      <span className="text-gray-700 text-xs font-bold uppercase tracking-[0.18em]">{title}</span>
      <span className="text-gray-500 text-xs mt-1 max-w-xs">{description}</span>
    </div>
  );
}